/* Product size unit switcher for both product implementations in the theme. */
(() => {
  const UNIT_BUTTONS = '[data-spx-size-unit], [data-unit]';
  let selectedUnit = 'cm';
  const normalizeUnit = (value) => String(value || '').toLowerCase() === 'in' ? 'in' : 'cm';

  const sourceValue = (element, fallbackUnit) => {
    if (!element) return '';
    if (!element.dataset.sourceUnitOriginal) {
      const input = element.closest('label')?.previousElementSibling;
      element.dataset.sourceUnitOriginal = element.dataset.optionValue
        || element.dataset.cmValue
        || input?.dataset.cmValue
        || element.textContent.trim();
      element.dataset.sourceUnitOriginalUnit = element.dataset.cmValue || input?.dataset.cmValue
        ? 'cm'
        : fallbackUnit;
    }
    return element.dataset.sourceUnitOriginal;
  };

  const formatNumber = (value) => {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',');
  };

  const convertLabel = (value, targetUnit, fallbackUnit = 'in') => {
    const raw = String(value || '').trim();
    const match = raw.match(/(\d+(?:[.,]\d+)?)\s*[xX×]\s*(\d+(?:[.,]\d+)?)(?:\s*(cm|in|inch|inches))?/i);
    if (!match) return raw;

    const sourceUnit = normalizeUnit(match[3] || fallbackUnit);
    const width = Number(match[1].replace(',', '.'));
    const height = Number(match[2].replace(',', '.'));
    if (!Number.isFinite(width) || !Number.isFinite(height)) return raw;

    const factor = sourceUnit === targetUnit ? 1 : sourceUnit === 'in' ? 2.54 : 1 / 2.54;
    return `${formatNumber(width * factor)} × ${formatNumber(height * factor)} ${targetUnit}`;
  };

  const replaceTextNode = (element, text) => {
    const textNode = [...element.childNodes].find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (textNode) {
      textNode.textContent = text;
    } else {
      element.insertBefore(document.createTextNode(text), element.firstChild);
    }
  };

  const renderSpxProduct = (product, unit) => {
    product.querySelectorAll('.spx-product__size-group').forEach((group) => {
      group.querySelectorAll('[data-spx-variant-value]').forEach((control) => {
        const original = sourceValue(control, 'in');
        replaceTextNode(control, convertLabel(original, unit, 'in'));
      });

      const output = group.querySelector('[data-spx-option-output]');
      if (output) {
        const selected = group.querySelector('[data-spx-variant-value].is-selected');
        const original = sourceValue(selected, 'in') || output.dataset.sourceUnitOriginal || output.textContent;
        output.dataset.sourceUnitOriginal = original;
        output.textContent = convertLabel(original, unit, 'in');
      }
    });
  };

  const renderLegacyProduct = (scope, unit) => {
    scope.querySelectorAll('[data-source-unit-label]').forEach((label) => {
      const input = label.closest('label')?.previousElementSibling;
      const original = sourceValue(label, 'cm') || input?.dataset.cmValue || label.textContent;
      label.textContent = convertLabel(original, unit, 'cm');
    });

    scope.querySelectorAll('[data-source-product-option-label]').forEach((output) => {
      const fieldset = output.closest('[data-source-product-option]');
      const selected = fieldset?.querySelector('[data-source-product-option-input]:checked');
      const original = selected?.dataset.cmValue || output.dataset.sourceUnitOriginal || output.textContent;
      output.dataset.sourceUnitOriginal = original;
      output.textContent = convertLabel(original, unit, 'cm');
    });
  };

  const applyUnit = (scope, unit, persist = false) => {
    const normalized = normalizeUnit(unit);
    const root = scope.matches?.('[data-spx-product], [data-source-product-purchase]') ? scope : scope;
    root.querySelectorAll(UNIT_BUTTONS).forEach((button) => {
      const buttonUnit = normalizeUnit(button.dataset.spxSizeUnit || button.dataset.unit);
      const selected = buttonUnit === normalized;
      button.classList.toggle('is-selected', selected);
      button.classList.toggle('active', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    renderSpxProduct(root, normalized);
    renderLegacyProduct(root, normalized);
    if (persist) selectedUnit = normalized;
  };

  const getProductScope = (element) => element.closest('[data-spx-product], [data-source-product-purchase], form') || document;

  const initialize = () => {
    const scopes = document.querySelectorAll('[data-spx-product], [data-source-product-purchase]');
    scopes.forEach((scope) => applyUnit(scope, selectedUnit));
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest?.(UNIT_BUTTONS);
    if (!button) return;
    event.preventDefault();
    applyUnit(getProductScope(button), button.dataset.spxSizeUnit || button.dataset.unit, true);
  });

  // Re-apply the selected unit after a variant change replaces the visible
  // size text with the product's original option value.
  document.addEventListener('spx:variant-change', (event) => {
    const scope = event.target.closest?.('[data-spx-product]');
    if (scope) applyUnit(scope, selectedUnit);
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[data-source-product-option-input]')) {
      const scope = getProductScope(event.target);
      window.setTimeout(() => applyUnit(scope, selectedUnit), 0);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
